import { useEffect, useRef } from "react"
import type { ReactNode } from "react"
import { cn } from "@/lib/cn"

// Lightweight modal built on the native <dialog> element. Gets focus trap
// + ESC-to-close for free. Styled to match Card (rounded-card, panel bg,
// border, shadow). Caller controls `open` + provides `onClose` for both
// the backdrop click and ESC.
type Props = {
  open: boolean
  onClose: () => void
  children: ReactNode
  className?: string
  size?: "sm" | "md" | "lg" | "xl"
}

const SIZE: Record<NonNullable<Props["size"]>, string> = {
  sm: "max-w-md",
  md: "max-w-xl",
  lg: "max-w-3xl",
  // For diff / transcript views — fills almost the whole viewport so
  // long prompt before/after blocks render side-by-side without
  // wrapping. Capped at 92vw via the dialog wrapper class.
  xl: "max-w-6xl",
}

export function Dialog({ open, onClose, children, className, size = "md" }: Props) {
  const ref = useRef<HTMLDialogElement | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (open && !el.open) el.showModal()
    if (!open && el.open) el.close()
  }, [open])

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => {
        // close on backdrop click — the dialog element itself receives the
        // click when the user clicks outside its content.
        if (e.target === ref.current) onClose()
      }}
      className={cn(
        "rounded-card border border-border bg-app-panel text-app-text shadow-card backdrop:bg-black/60",
        "p-0 w-[92vw]",
        SIZE[size],
        className,
      )}
    >
      {children}
    </dialog>
  )
}

export function DialogHeader({ title, subtitle }: { title: string; subtitle?: ReactNode }) {
  return (
    <header className="border-b border-border px-6 py-4">
      <h2 className="text-lg font-semibold">{title}</h2>
      {subtitle && <div className="mt-1 text-sm text-app-muted">{subtitle}</div>}
    </header>
  )
}

export function DialogBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("px-6 py-5 space-y-4", className)}>{children}</div>
}

export function DialogFooter({ children }: { children: ReactNode }) {
  return (
    <footer className="flex justify-end gap-3 border-t border-border px-6 py-4">
      {children}
    </footer>
  )
}
